<script setup>
import { computed, onBeforeUnmount, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import SalesRegistrationPermissionState from '../components/SalesRegistrationPermissionState.vue';
import SalesDeliveryPageContent from '../components/SalesDeliveryPageContent.vue';
import SalesDeliveryPageHeader from '../components/SalesDeliveryPageHeader.vue';
import SalesDeliverySubmissionDialog from '../components/SalesDeliverySubmissionDialog.vue';
import {
  salesDeliveryReturnQuery,
  salesReviewLocation,
  shouldReturnToSalesReview,
} from '../composables/salesReviewNavigation';
import { useSalesDelivery } from '../composables/useSalesDelivery';
import { useSalesReceiptAnalysis } from '../composables/useSalesReceiptAnalysis';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const id = computed(() => route.params.salesTransactionId);
const delivery = useSalesDelivery({ id, t, notify: useAlert });
const receiptAnalysis = useSalesReceiptAnalysis({ id, t, notify: useAlert });
const transaction = computed(() => delivery.transaction.value);
const canDeliver = computed(
  () => delivery.canDeliver.value && !receiptAnalysis.hasPendingConflicts.value
);
const submissionOpen = ref(false);
const submissionStatus = ref('sending');
let checkoutTimer;
const goBack = () => {
  if (shouldReturnToSalesReview(route)) {
    return router.push(
      salesReviewLocation({
        accountId: route.params.accountId,
        salesTransactionId: id.value,
      })
    );
  }
  return router.push({
    name: 'operacao_imobiliaria_vendas_cadastro',
    params: { accountId: route.params.accountId, salesTransactionId: id.value },
  });
};
const save = () => delivery.save();
const showSummary = () =>
  router.push({
    name: 'operacao_imobiliaria_vendas_entregar_resumo',
    params: { accountId: route.params.accountId, salesTransactionId: id.value },
  });
const goToCheckout = () =>
  router.push({
    name: 'operacao_imobiliaria_vendas_entregar_checkout',
    params: { accountId: route.params.accountId, salesTransactionId: id.value },
  });
const closeSubmission = () => {
  submissionOpen.value = false;
  submissionStatus.value = 'sending';
};
const deliver = async () => {
  if (!delivery.validate()) {
    await delivery.deliver();
    return;
  }
  const isDocuSign = delivery.form.value.recibo_type === 'docusign';
  if (isDocuSign) {
    submissionStatus.value = 'sending';
    submissionOpen.value = true;
  }
  if (!(await delivery.deliver())) {
    if (isDocuSign) submissionStatus.value = 'error';
    return;
  }
  if (isDocuSign) {
    submissionStatus.value = 'success';
    checkoutTimer = setTimeout(goToCheckout, 3000);
    return;
  }
  router.push({
    name: 'operacao_imobiliaria_vendas',
    params: { accountId: route.params.accountId },
  });
};
const retryDelivery = () => deliver();
const editConflict = conflict => {
  const base = {
    accountId: route.params.accountId,
    salesTransactionId: id.value,
  };
  if (conflict.type === 'imovel') {
    router.push({
      name: 'operacao_imobiliaria_vendas_cadastro_imovel',
      params: base,
      query: salesDeliveryReturnQuery,
    });
    return;
  }
  const party = delivery.participants.value.find(
    item => String(item.id) === String(conflict.reference_id)
  );
  if (party) {
    const partyRole =
      party.party_role ||
      party.role ||
      (conflict.type === 'vendedores' ? 'vendedor' : 'comprador');
    const representative = party.relation_type === 'representante';
    router.push({
      name: representative
        ? 'operacao_imobiliaria_vendas_cadastro_representante'
        : 'operacao_imobiliaria_vendas_cadastro_parte',
      params: {
        ...base,
        partyId: party.id,
        partyRole,
        ...(representative
          ? { parentPartyId: party.parent_party_id }
          : { partyKind: party.party_kind }),
      },
      query: salesDeliveryReturnQuery,
    });
    return;
  }
  router.push({ name: 'operacao_imobiliaria_vendas_cadastro', params: base });
};

onBeforeUnmount(() => clearTimeout(checkoutTimer));
</script>

<template>
  <div class="min-h-full bg-lais-bg-soft">
    <SalesRegistrationPermissionState
      v-if="delivery.isForbidden.value"
      @back="router.back()"
    />
    <template v-else>
      <SalesDeliveryPageHeader
        :can-deliver="canDeliver"
        :can-save="delivery.canSave.value"
        :can-view-summary="Boolean(transaction)"
        :delivering="delivery.delivering.value"
        :saving="delivery.saving.value"
        @back="goBack"
        @deliver="deliver"
        @save="save"
        @summary="showSummary"
      />
      <SalesDeliveryPageContent
        :delivery="delivery"
        :receipt-analysis="receiptAnalysis"
        :transaction="transaction"
        @confirm-conflict="
          receiptAnalysis.resolve($event, 'confirmed_registration')
        "
        @correct-conflict="receiptAnalysis.resolve($event, 'corrected')"
        @edit-conflict="editConflict"
        @update="delivery.update"
        @update-receipt="delivery.update('recibo_type', $event)"
        @update-witness="delivery.updateWitness"
      />
      <SalesDeliverySubmissionDialog
        :show="submissionOpen"
        :status="submissionStatus"
        @close="closeSubmission"
        @retry="retryDelivery"
      />
    </template>
  </div>
</template>
